/*
 * preen-pf-runtime.js - Preen Pets storefront runtime (unit pf-runtime, 2026-09-28; 1.2.0 fix-layout-6, 2026-10-01; 1.2.1 its revision 2).
 * Gives the page-builder markup in our sections (data-pf-type, pf-* classes, and the inline data
 * window.__pagefly_setting__.elementData, window.__pagefly_page_setting__, window.__pageflyProducts)
 * the behaviour the builder's app-embed scripts gave it, in plain JavaScript, and only what our
 * pages use: clicks ([data-href]), productForm (add to cart), variants, gallery, slider (Slideshow),
 * video, lazy (.pf-bg-lazy, data-src), description (clean-up, data-compact word limit) and, on the
 * home page only, review links (each Judge.me carousel card links to where its review was posted).
 * No jQuery, no cookies. Requests: this store's /cart/add.js, /?sections= when a Dawn cart drawer or
 * notification exists, and on the home page only the public review list on cdn.judge.me (the host
 * the Judge.me widget already loads from), read once per visit and kept in sessionStorage; no
 * other storage. Loading it twice is harmless.
 * Runs on DOMContentLoaded, again on window load (markup replaced after DOMContentLoaded) and on
 * shopify:section:load.
 * window.PreenPF = { version, init(root), loadAll(), pause(), resume(), resetSliders() }:
 *   loadAll() reveals lazy images/backgrounds now and loads lazy videos that are shown;
 *   pause() stops slideshow autoplay and pauses videos (resume() restarts autoplay);
 *   resetSliders() stops autoplay and puts every slideshow and gallery on its first slide.
 */
(function () {
  'use strict';
  if (window.PreenPF && window.PreenPF.version) return;

  var VERSION = '1.2.1';
  var doc = document;
  var HIDDEN = 'pf-visibility-hidden';
  var hasIO = 'IntersectionObserver' in window;

  /* ------------------------------------------------------------------ helpers */
  var bound = new WeakMap();                      // element -> set of modules already bound to it
  function once(el, key) {
    var s = bound.get(el);
    if (!s) { s = new Set(); bound.set(el, s); }
    if (s.has(key)) return false;
    s.add(key);
    return true;
  }
  function all(root, sel) {
    var out = root && root.querySelectorAll ? Array.prototype.slice.call(root.querySelectorAll(sel)) : [];
    if (root && root.matches && root.matches(sel)) out.unshift(root);
    return out;
  }
  function kids(el, sel) {
    return el ? Array.prototype.filter.call(el.children, function (c) { return c.matches(sel); }) : [];
  }
  function kid(el, sel) { return kids(el, sel)[0] || null; }
  function safe(fn, arg) {
    try { return fn(arg); } catch (e) { if (window.console) console.warn('[PreenPF]', e); }
  }
  function debounce(fn, ms) {
    var t = 0;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }
  function num(v, d) { v = Number(v); return isFinite(v) ? v : d; }
  function fire(el, type) { el.dispatchEvent(new Event(type, { bubbles: true })); }
  function shown(el) { return !!(el && el.getClientRects().length); }
  function vis(el, on) { if (el) el.style.visibility = on ? '' : 'hidden'; }
  // Arrow and dot buttons: the click stops there (the block around may be a link); double clicks count once
  function onPress(el, fn) {
    if (el) el.addEventListener('click', function (e) { e.stopPropagation(); if (e.detail <= 1) fn(); });
  }
  function cartRoot() { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; }
  // IntersectionObserver that runs fn(element) once, the first time the element comes into view
  function watch(fn, opts) {
    if (!hasIO) return null;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { io.unobserve(en.target); fn(en.target); } });
    }, opts);
    return io;
  }

  // Settings of one element: the class pf-<n>_ (or pf-<id>-<n>_) keys window.__pagefly_setting__.elementData
  var PF_KEY = /^pf-(?:\d+|[a-z0-9]+-\d+)_$/;
  function conf(el) {
    var list = el && el.classList, data = window.__pagefly_setting__ && window.__pagefly_setting__.elementData;
    if (!list || !data) return {};
    for (var i = 0; i < list.length; i++) if (PF_KEY.test(list[i]) && data[list[i]]) return data[list[i]];
    return {};
  }
  // The builder's device bands (whole-pixel window widths): 0-767, 768-1024, 1025-1200, wider
  function device() {
    var w = window.innerWidth;
    return w <= 767 ? 'mobile' : w <= 1024 ? 'tablet' : w <= 1200 ? 'laptop' : 'all';
  }
  function perDevice(v, d) {
    if (!v || typeof v !== 'object') return v;
    d = d || device();
    return v[d] !== undefined ? v[d] : v.all;
  }
  function products() { return window.__pageflyProducts || {}; }
  function productOf(el) {
    var id = el && el.getAttribute('data-product-id');
    return id ? products()[id] || null : null;
  }
  function variantById(p, id) {
    if (!p || !p.variants || id == null) return null;
    for (var i = 0; i < p.variants.length; i++) if (String(p.variants[i].id) === String(id)) return p.variants[i];
    return null;
  }
  function pageType() {
    var s = window.__pagefly_page_setting__ || window.__pagefly_setting__ || {};
    return s.pageType;
  }
  function urlVariant() {
    var m = /[?&]variant=(\d+)/.exec(location.search);
    return m ? m[1] : null;
  }

  // Shopify money formats, as the section's price Liquid renders them (format string may hold HTML)
  var MONEY = {
    amount: [2, ',', '.'], amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','], amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'], amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ' '], amount_with_period_and_space_separator: [2, ' ', '.']
  };
  function money(cents, format) {
    var fmt = format || (window.__pagefly_setting__ && window.__pagefly_setting__.moneyFormat) || '{{amount}}';
    var m = /\{\{\s*(\w+)\s*\}\}/.exec(fmt);
    if (!m) return fmt;
    var o = MONEY[m[1]] || MONEY.amount;
    var parts = (num(cents, 0) / 100).toFixed(o[0]).split('.');
    var text = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, o[1]) + (parts[1] ? o[2] + parts[1] : '');
    return fmt.replace(m[0], function () { return text; });
  }

  // Scroll position that puts a slide at the track's snap start; nearest slide to the current position
  function slideLeft(track, el) {
    var pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    var x = el.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - pad;
    return Math.max(0, Math.min(track.scrollWidth - track.clientWidth, x));
  }
  function nearest(track, slides) {
    var best = 0, gap = Infinity, x = track.scrollLeft;
    for (var i = 0; i < slides.length; i++) {
      var d = Math.abs(slideLeft(track, slides[i]) - x);
      if (d < gap - 1) { gap = d; best = i; }
    }
    return best;
  }
  function goTo(track, slides, i, smooth) {
    var el = slides[i];
    if (!el || !shown(track)) return;
    track.scrollTo({ left: slideLeft(track, el), behavior: smooth ? 'smooth' : 'instant' });
  }
  // Arrow visibility for a non-looping strip (hidden at the ends, both hidden when all slides fit)
  function navState(prev, next, count, show, step, cur) {
    if (!prev || !next) return;
    var pages = Math.ceil((count - show) / step) + 1, page = Math.ceil(cur / step);
    if (count <= show) { vis(next, false); vis(prev, false); }
    else if (page >= pages - 1) { vis(next, false); vis(prev, true); }
    else if (page === 0) { vis(next, true); vis(prev, false); }
    else { vis(next, true); vis(prev, true); }
  }
  function dotsState(buttons, cur, step) {
    var active = Math.min(Math.ceil(cur / step), buttons.length - 1);
    buttons.forEach(function (b, i) { b.classList.toggle('active', i === active); });
  }

  /* ------------------------------------------------------------------ clicks (data-href) */
  // One document listener. The innermost [data-href] block wins; a link, button, field or a
  // review-carousel arrow inside the block keeps its own job.
  var CONTROL = 'a[href],button,input,select,textarea,label,summary,[role="button"],[contenteditable="true"],' +
    'video[controls],.jdgm-carousel__arrows,[class*="jdgm-carousel__"][class*="arrow"]';
  function onDataHref(e) {
    var t = e.target;
    if (t && t.nodeType !== 1) t = t.parentElement;
    var el = t && t.closest ? t.closest('[data-pf-type][data-href]') : null;
    if (!el) return;
    var href = (el.getAttribute('data-href') || '').trim();
    var act = el.getAttribute('data-action');
    if (!href || (act && act !== 'url' && act !== 'phone' && act !== 'email')) return;
    var ctl = t.closest(CONTROL);
    if (ctl && el.contains(ctl)) return;
    openUrl(href, e.ctrlKey || e.metaKey ? '_blank' : el.getAttribute('data-target') || '_self');
  }
  function openUrl(href, target) {
    if (target === '_self') window.location.assign(href);
    else if (target === '_blank') window.open(href, '_blank', 'noopener');
    else window.open(href, target);
  }

  /* ------------------------------------------------------------------ productForm (add to cart) */
  var atcLabel = new WeakMap();                   // button -> its markup before any change
  var busy = new WeakSet();                       // buttons with an add in flight
  var current = new WeakMap();                    // ProductBox -> variant object in use
  var ATC = '[data-pf-type="ProductATC2"],[data-pf-type="ProductATC"]';
  function bindAtc(root) {
    all(root, ATC).forEach(function (btn) {
      if (!once(btn, 'atc')) return;
      atcLabel.set(btn, btn.innerHTML);
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!btn.disabled && !busy.has(btn)) addToCart(btn);
      });
    });
  }
  function variantFor(btn, p) {
    var box = btn.closest('[data-pf-type="ProductBox"]');
    return (box && current.get(box)) || variantById(p, btn.getAttribute('data-current-variant')) ||
      (p && (p.selected_or_first_available_variant || (p.variants && p.variants[0]))) || null;
  }
  function addToCart(btn) {
    var p = productOf(btn), form = btn.closest('form'), body, v;
    if (form) {
      if (!form.checkValidity()) { form.reportValidity(); return; }
      body = new FormData(form);                  // id, quantity, selling_plan (Seal), properties
      var idIn = form.querySelector('[name="id"]');
      if (!(idIn && Number(idIn.value))) {
        v = variantFor(btn, p);
        if (!v) return;
        body.set('id', v.id);
      }
    } else {
      v = variantFor(btn, p);                     // a button outside any product form
      if (!v) return;
      var box = btn.closest('[data-pf-type="ProductBox"]');
      var q = box && box.querySelector('[data-pf-type="ProductQuantity"] input');
      body = new FormData();
      body.append('quantity', (q && q.value) || 1);
      body.append('id', v.id);
    }
    var label = atcLabel.has(btn) ? atcLabel.get(btn) : btn.innerHTML;
    var note = btn.querySelector('span') ? null : doc.createElement('span');
    if (note) { btn.textContent = ''; btn.appendChild(note); } else note = btn.querySelector('span');
    note.textContent = btn.getAttribute('data-adding') || '';
    btn.setAttribute('disabled', 'true');
    busy.add(btn);
    fetch(cartRoot() + 'cart/add.js', {
      method: 'POST', body: body, credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, j: j || {} }; });
    }).then(function (res) {
      if (!res.ok || res.j.status >= 400) { atcError(btn, res.j.description || res.j.message); return; }
      note.textContent = btn.getAttribute('data-added') || '';
      afterAdd(btn, res.j);
    }).catch(function () {
      atcError(btn, '');
    }).then(function () {
      setTimeout(function () { restoreAtc(btn, label); }, 1500);
    });
  }
  function restoreAtc(btn, label) {
    busy.delete(btn);
    btn.innerHTML = label;
    btn.removeAttribute('disabled');
    var box = btn.closest('[data-pf-type="ProductBox"]');
    if (box && current.get(box)) atcState(btn, current.get(box));
  }
  function atcError(btn, text) {
    var p = doc.createElement('p');
    p.style.color = 'red';
    p.style.marginTop = '15px';
    p.textContent = '* ' + (text || 'Could not add to cart');
    (btn.parentElement || btn).appendChild(p);
    setTimeout(function () { p.remove(); }, 4000);
  }
  function afterAdd(btn, item) {
    var mode = btn.getAttribute('data-checkout');
    if (mode === 'cart') { window.location.assign(cartRoot() + 'cart'); return; }
    if (mode === 'link' && btn.getAttribute('data-checkout-link')) { window.location.assign(btn.getAttribute('data-checkout-link')); return; }
    doc.dispatchEvent(new CustomEvent('preenpf:cart-added', { detail: item }));
    // 'same': stay on the page. Dawn's cart drawer / notification, when the theme renders one, is refreshed
    var ui = doc.querySelector('cart-notification') || doc.querySelector('cart-drawer');
    if (!ui || typeof ui.renderContents !== 'function') return;
    fetch(cartRoot() + '?sections=cart-notification-product,cart-notification-button,cart-drawer,cart-icon-bubble',
      { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (sections) {
      item.sections = sections;
      ui.classList.remove('is-empty');
      ui.renderContents(item);
    }).catch(function () {});
  }

  /* ------------------------------------------------------------------ variants */
  function inventory(p) {
    var out = {};
    (p.quantity || []).forEach(function (s) {
      var i = String(s).indexOf(':');
      if (i > 0) out[String(s).slice(0, i)] = parseInt(String(s).slice(i + 1), 10);
    });
    return out;
  }
  function atcState(b, v) {
    if (busy.has(b)) return;
    if (v && v.available) {
      b.removeAttribute('disabled');
      if (atcLabel.has(b) && b.innerHTML !== atcLabel.get(b)) b.innerHTML = atcLabel.get(b);
      return;
    }
    b.setAttribute('disabled', 'true');
    var text = v ? b.getAttribute('data-soldout') || 'Sold Out' : window.__pf_unavailable_text || 'Unavailable';
    var span = b.querySelector('span');
    if (!span) { b.textContent = ''; span = b.appendChild(doc.createElement('span')); }
    span.textContent = text;
  }
  function setHtml(el, html) { if (el.innerHTML !== html) el.innerHTML = html; }
  // Show variant v of product p in its ProductBox (or in the swatches block when there is no box)
  function applyVariant(scope, p, v, opt) {
    current.set(scope, v);
    all(scope, 'input[name="id"], .pf-variant-' + p.id).forEach(function (inp) {
      var own = inp.getAttribute('data-product-id');
      if ((own && own !== String(p.id)) || String(inp.value) === String(v.id)) return;
      inp.value = v.id;
      if (!opt.init) { fire(inp, 'input'); fire(inp, 'change'); }   // Seal and other apps follow input[name=id]
    });
    if (typeof v.price === 'number') all(scope, '[data-product-type="price"]').forEach(function (el) { setHtml(el, money(v.price)); });
    all(scope, '[data-product-type="compare_at_price"]').forEach(function (el) {
      setHtml(el, num(v.compare_at_price, 0) > num(v.price, 0) ? money(v.compare_at_price) : '');
    });
    var stock = inventory(p);
    all(scope, '[data-pf-type="QuantityField"]').forEach(function (q) {
      var n = stock[v.id], keeps = (q.getAttribute('data-variants-continue') || '').indexOf(String(v.id)) >= 0;
      if (keeps || (n <= 0 && v.available)) q.removeAttribute('max');
      else if (isFinite(n)) q.setAttribute('max', n);
    });
    all(scope, ATC).forEach(function (b) { atcState(b, v); });
    if (opt.sync) all(scope, '[data-pf-type="ProductVariantSwatches"]').forEach(function (sw) { syncSwatches(sw, v); });
    if (opt.media && v.featured_media) {
      all(scope, '[data-pf-type="ProductMedia2"],[data-pf-type="ProductMedia3"]').forEach(function (pm) {
        if (pm.getAttribute('data-product-id') === String(p.id)) galleryShowMedia(pm, v.featured_media.id);
      });
    }
  }
  function swatchGroups(sw) { return all(sw, '.pf-option-swatches'); }
  function syncSwatches(sw, v) {
    swatchGroups(sw).forEach(function (g, i) {
      var val = v.options && v.options[i];
      if (val == null) return;
      var sel = g.querySelector('select');
      if (sel) { sel.value = val; return; }
      all(g, 'input[type="radio"]').forEach(function (r) { if (r.value === val) r.checked = true; });
    });
  }
  function bindSwatches(root) {
    all(root, '[data-pf-type="ProductVariantSwatches"]').forEach(function (sw) {
      if (sw.childElementCount <= 1 || !once(sw, 'swatches')) return;
      sw.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || t.name === 'id' || !t.matches('input[type="radio"],select')) return;
        var p = productOf(sw);
        if (!p || !p.variants) return;
        var v = null;
        if (sw.getAttribute('data-combined') === 'true') v = variantById(p, t.value);
        else {
          var vals = swatchGroups(sw).map(function (g) {
            var s = g.querySelector('select'), r = g.querySelector('input:checked');
            return s ? s.value : r ? r.value : null;
          });
          var title = vals.join(' / ');
          p.variants.forEach(function (x) {
            if (!v && (x.title === title || (x.options && x.options.join(' / ') === title))) v = x;
          });
        }
        if (!v) return;
        applyVariant(sw.closest('[data-pf-type="ProductBox"]') || sw, p, v, { media: true });
        if (pageType() === 'product') {
          var u = new URL(window.location.href);
          u.searchParams.set('variant', v.id);
          history.replaceState(history.state, '', u.href);
        }
      });
    });
  }
  // First state of every product box: ?variant= on a product page, else the box default, else the
  // first available variant. Prices, id, sold-out state and quantity max follow it.
  function initVariants(root) {
    all(root, '[data-pf-type="ProductBox"]').forEach(function (box) {
      var p = productOf(box);
      if (!p || !p.variants || !p.variants.length || !once(box, 'variant')) return;
      var fromUrl = pageType() === 'product' ? variantById(p, urlVariant()) : null;
      var v = fromUrl || variantById(p, box.getAttribute('data-default-variant')) ||
        p.selected_or_first_available_variant || p.variants[0];
      if (v) applyVariant(box, p, v, { init: true, sync: true, media: !!fromUrl });
    });
  }
  /* ------------------------------------------------------------------ gallery (ProductMedia2) */
  var galleries = [];
  function galleryOf(pm) { for (var i = 0; i < galleries.length; i++) if (galleries[i].pm === pm) return galleries[i]; return null; }
  function initGalleries(root) {
    all(root, '[data-pf-type="ProductMedia2"],[data-pf-type="ProductMedia3"]').forEach(function (pm) {
      var main = pm.querySelector('[data-pf-type^="MediaMain"]');
      var mt = main && main.querySelector('.pf-media-slider');
      if (!mt || !once(pm, 'gallery')) return;
      // Liquid may put the ?variant= photo first and park the original first photo in a <template>
      var tpl = mt.querySelector('template');
      if (tpl && mt.firstElementChild && mt.firstElementChild !== tpl) { mt.replaceChild(tpl.content.cloneNode(true), mt.firstElementChild); tpl.remove(); }
      var list = pm.querySelector('[data-pf-type="MediaList2"]'), lt = list && list.querySelector('.pf-media-slider');
      var g = { pm: pm, main: main, mt: mt, lt: lt, mc: conf(main), lc: conf(list), p: productOf(pm), cur: 0 };
      g.slides = kids(mt, '.pf-slide-main-media');
      g.thumbs = lt ? kids(lt, '.pf-slide-list-media') : [];
      if (!g.slides.length) return;
      galleries.push(g);
      var d = device(), mc = g.mc, lc = g.lc;
      g.step = Math.max(1, num(perDevice(mc.slidesToScroll, d), 1));
      g.show = Math.max(1, num(perDevice(mc.slidesToShow, d), 1));
      mt.style.scrollSnapType = 'x mandatory';
      g.slides.forEach(function (s, i) { if (i % g.step === 0) s.style.scrollSnapStop = 'always'; });
      g.listSlides = !!lt && num(perDevice(lc.listLayout, d), 0) === 1;
      if (g.listSlides) {
        lt.style.scrollSnapType = 'x mandatory';
        var ls = Math.max(1, num(perDevice(lc.slidesToScroll, d), 1));
        g.thumbs.forEach(function (s, i) { if (i % ls === 0) s.style.scrollSnapStop = 'always'; });
      }
      if (mc.disableSlideshow) originalRatio(g);
      // arrows and dots of the main strip
      g.prev = mt.querySelector('.pf-slider-prev');
      g.next = mt.querySelector('.pf-slider-next');
      onPress(g.next, function () { galleryGo(g, g.cur + g.step); });
      onPress(g.prev, function () { galleryGo(g, g.cur - g.step); });
      var nav = kid(mt, '.pf-slider-nav');
      g.dots = nav && mc.paginationStyle && mc.paginationStyle !== 'none' ? kids(nav, 'button') : [];
      g.dots.forEach(function (b, i) { onPress(b, function () { galleryGo(g, i * g.step); }); });
      // the thumbnail strip's own arrows move the main photo (by the strip's step)
      if (lt) {
        g.lprev = lt.querySelector('.pf-slider-prev');
        g.lnext = lt.querySelector('.pf-slider-next');
        var lstep = Math.max(1, num(perDevice(lc.slidesToScroll, d), 1));
        onPress(g.lnext, function () { galleryGo(g, g.cur + lstep); });
        onPress(g.lprev, function () { galleryGo(g, g.cur - lstep); });
        lt.addEventListener('scroll', debounce(function () { markThumbs(g); }, 100), { passive: true });
      }
      g.thumbs.forEach(function (th, i) {
        th.addEventListener('click', function () {
          g.thumbs.forEach(function (x) { x.removeAttribute('data-active'); });
          th.setAttribute('data-active', 'true');
          if (!mc.disableSlideshow) galleryGo(g, i);
        });
      });
      mt.addEventListener('scroll', debounce(function () { g.cur = nearest(mt, g.slides); markGallery(g); }, 100), { passive: true });
      if (num(mc.hoverAction, 0) === 1) bindZoom(g);
      if (num(mc.clickAction, 0) === 2) bindViewer(g);
      var start = startIndex(g);
      if (start > 0 && !mc.disableSlideshow) goTo(mt, g.slides, start, false);
      g.cur = mc.disableSlideshow ? 0 : start;
      g.thumbCur = mc.disableSlideshow ? start : g.cur;
      markGallery(g);
      markThumbs(g);
    });
  }
  // Which photo a gallery opens on: the ?variant= photo (or the first variant's, for imageSource 'variant')
  function startIndex(g) {
    var p = g.p, src = conf(g.pm).imageSource || 'featured';
    if (!p || !p.variants || !p.media) return 0;
    var id = urlVariant(), k = -1;
    if (src === 'default-variant' && !id) {
      var box = g.pm.closest('[data-pf-type="ProductBox"]');
      var dv = box && variantById(p, box.getAttribute('data-default-variant'));
      return dv && dv.featured_media ? Math.max(0, num(dv.featured_media.position, 1) - 1) : 0;
    }
    if (id) p.variants.forEach(function (x, i) { if (k < 0 && String(x.id) === id) k = i; });
    var v = p.variants[k > 0 ? k : 0];
    if (!(src === 'variant' || k >= 0) || !v || !v.featured_media) return 0;
    var idx = -1;
    p.media.forEach(function (m, i) { if (idx < 0 && m.id === v.featured_media.id) idx = i; });
    return idx > 0 && idx < g.slides.length ? idx : 0;
  }
  function galleryGo(g, i) { goTo(g.mt, g.slides, Math.max(0, Math.min(i, g.slides.length - 1)), true); }
  function galleryShowMedia(pm, mediaId) {
    var g = galleryOf(pm);
    if (!g || g.mc.disableSlideshow) return;
    g.slides.forEach(function (s, i) { if (s.getAttribute('data-media-id') === String(mediaId) && i !== g.cur) galleryGo(g, i); });
  }
  // Active photo: is-current on the slide, data-active="true" on the slide and its thumbnail
  // (the compiled CSS draws the blue thumbnail border from [data-active=true])
  function markGallery(g) {
    var a = g.mc.disableSlideshow ? -1 : g.cur;
    g.slides.forEach(function (s, i) {
      var on = g.mc.disableSlideshow ? i === 0 : i === a;
      s.classList.toggle('is-current', on);
      if (on) s.setAttribute('data-active', 'true'); else s.removeAttribute('data-active');
    });
    if (!g.mc.disableSlideshow) {
      g.thumbs.forEach(function (t, i) { if (i === a) t.setAttribute('data-active', 'true'); else t.removeAttribute('data-active'); });
      var th = g.thumbs[a];
      if (th && g.listSlides && shown(g.lt)) {
        var tr = th.getBoundingClientRect(), lr = g.lt.getBoundingClientRect();
        if (tr.left < lr.left - 1 || tr.right > lr.right + 1) goTo(g.lt, g.thumbs, a, true);
      }
    } else if (g.thumbs[g.thumbCur] && !g.thumbs.some(function (t) { return t.hasAttribute('data-active'); })) {
      g.thumbs[g.thumbCur].setAttribute('data-active', 'true');
    }
    if (g.mc.navStyle && g.mc.navStyle !== 'none') navState(g.prev, g.next, g.slides.length, g.show, g.step, g.cur);
    if (g.dots.length) dotsState(g.dots, g.cur, g.step);
    var inner = g.pm.querySelector('.product-media-loading');
    if (inner) inner.classList.remove('product-media-loading');
  }
  function markThumbs(g) {
    if (!g.thumbs.length) return;
    var c = g.listSlides && shown(g.lt) ? nearest(g.lt, g.thumbs) : g.thumbCur || 0;
    g.thumbs.forEach(function (t, i) { t.classList.toggle('is-current', i === c); });
    if (g.listSlides && g.lc.navStyle && g.lc.navStyle !== 'none') {
      var d = device();
      navState(g.lprev, g.lnext, g.thumbs.length, Math.max(1, num(perDevice(g.lc.slidesToShow, d), 1)),
        Math.max(1, num(perDevice(g.lc.slidesToScroll, d), 1)), c);
    }
  }
  // Single-photo tiles (disableSlideshow) with ratio 'original': the photo keeps its own shape
  function originalRatio(g) {
    var r = g.mc.ratio || {}, v = r[device()];
    if ((v === undefined || v === 'unset' ? r.all : v) !== 'original') return;
    var m0 = g.p && g.p.media && g.p.media[0], ar = m0 && m0.preview_image && m0.preview_image.aspect_ratio;
    var img = g.mt.querySelector('.variant-image'), wrap = g.mt.querySelector('.pf-media-wrapper');
    if (!img || !ar) return;
    img.style.aspectRatio = ar;
    img.style.objectFit = 'contain';
    img.style.objectPosition = 'center top';
    if (wrap) wrap.style.paddingBottom = 100 / ar + '%';
  }
  // Hover zoom (hoverAction 1) on laptop and desktop widths: the photo becomes a 200% background
  function bindZoom(g) {
    g.slides.forEach(function (s) {
      var img = kid(s, 'img');
      if (!img) return;
      var ok = function () { return window.innerWidth >= 1025 && (g.mc.disableSlideshow || s.getAttribute('data-media-type') === 'image'); };
      s.addEventListener('mouseenter', function () {
        if (!ok()) return;
        img.style.opacity = '0';
        s.style.backgroundImage = 'url("' + (img.currentSrc || img.src) + '")';
        s.style.backgroundRepeat = 'no-repeat';
        s.style.backgroundSize = '200%';
      });
      s.addEventListener('mousemove', function (e) {
        if (!s.style.backgroundImage) return;
        var r = s.getBoundingClientRect();
        s.style.backgroundPosition = ((e.clientX - r.left) / r.width * 100) + '% ' + ((e.clientY - r.top) / r.height * 100) + '%';
      });
      s.addEventListener('mouseleave', function () {
        img.style.opacity = '';
        s.style.backgroundImage = '';
      });
    });
  }
  // Large view (clickAction 2): a click on a photo opens it full screen; arrows, Esc and the backdrop
  function bindViewer(g) {
    g.slides.forEach(function (s) {
      s.addEventListener('click', function () {
        if (s.getAttribute('data-media-type') !== 'image') return;
        var list = g.slides.filter(function (x) { return x.getAttribute('data-media-type') === 'image' && kid(x, 'img'); });
        var srcs = list.map(function (x) { var i = kid(x, 'img'); return i.getAttribute('src') || i.currentSrc; });
        openViewer(srcs, Math.max(0, list.indexOf(s)));
      });
    });
  }
  function openViewer(srcs, at) {
    if (!srcs.length) return;
    var back = doc.activeElement, root = doc.documentElement, overflow = root.style.overflow;
    var box = doc.createElement('div');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Photo');
    box.style.cssText = 'position:fixed;inset:0;z-index:2147483000;background:rgba(0,0,0,.9);display:flex;align-items:center;justify-content:center';
    var img = box.appendChild(doc.createElement('img'));
    img.alt = '';
    img.style.cssText = 'max-width:92vw;max-height:88vh;object-fit:contain';
    var btn = function (label, text, css) {
      var b = box.appendChild(doc.createElement('button'));
      b.type = 'button';
      b.setAttribute('aria-label', label);
      b.textContent = text;
      b.style.cssText = 'position:absolute;background:none;border:0;color:#fff;cursor:pointer;font:36px/1 sans-serif;padding:12px;' + css;
      return b;
    };
    var shut = btn('Close', '\u00d7', 'top:8px;right:8px');
    var prev = srcs.length > 1 ? btn('Previous photo', '\u2039', 'left:8px;top:50%;transform:translateY(-50%)') : null;
    var next = srcs.length > 1 ? btn('Next photo', '\u203a', 'right:8px;top:50%;transform:translateY(-50%)') : null;
    var show = function (i) { at = (i + srcs.length) % srcs.length; img.src = srcs[at]; };
    var close = function () {
      box.remove();
      root.style.overflow = overflow;
      doc.removeEventListener('keydown', keys);
      if (back && back.focus) back.focus();
    };
    var keys = function (e) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft' && prev) show(at - 1);
      else if (e.key === 'ArrowRight' && next) show(at + 1);
    };
    box.addEventListener('click', function (e) {
      if (e.target === box || e.target === shut) close();
      else if (e.target === prev) show(at - 1);
      else if (e.target === next) show(at + 1);
    });
    doc.addEventListener('keydown', keys);
    show(at);
    root.style.overflow = 'hidden';
    doc.body.appendChild(box);
    shut.focus();
  }

  /* ------------------------------------------------------------------ slider (Slideshow) */
  var shows = [];
  var motionPaused = false;
  function initSlideshows(root) {
    all(root, '[data-pf-type="Slideshow"]').forEach(function (el) {
      var track = el.querySelector('.pf-slider');
      if (!track || !once(el, 'slideshow')) return;
      var s = { el: el, track: track, c: conf(el), slides: kids(track, '.pf-slide'), cur: 0, timer: 0, hover: false };
      if (!s.slides.length) return;
      shows.push(s);
      var c = s.c, st = counts(s);
      s.slides.forEach(function (sl, i) { if (i % st.step === 0) sl.style.scrollSnapStop = 'always'; });
      s.prev = kid(track, '.pf-slider-prev');
      s.next = kid(track, '.pf-slider-next');
      onPress(s.next, function () { showStep(s, 1); });
      onPress(s.prev, function () { showStep(s, -1); });
      var nav = kid(track, '.pf-slider-nav');
      s.dots = [];
      if (nav && c.paginationStyle && c.paginationStyle !== 'none') {
        nav.textContent = '';
        var pages = Math.max(1, Math.ceil((s.slides.length - st.show) / st.step) + 1);
        for (var i = 0; i < pages; i++) {
          var b = nav.appendChild(doc.createElement('button'));
          b.type = 'button';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          onPress(b, goTo.bind(null, s.track, s.slides, Math.min(i * st.step, s.slides.length - 1), true));
          s.dots.push(b);
        }
      }
      track.addEventListener('scroll', debounce(function () { showSync(s); }, 100), { passive: true });
      if (c.pauseOnHover) {
        track.addEventListener('mouseover', function () { s.hover = true; });
        track.addEventListener('mouseleave', function () { s.hover = false; });
      }
      showSync(s);
      if (c.autoPlay) startAuto(s);
    });
  }
  function counts(s) {
    var d = device();
    return { n: s.slides.length, show: Math.max(1, num(perDevice(s.c.slidesToShow, d), 1)), step: Math.max(1, num(perDevice(s.c.slidesToScroll, d), 1)) };
  }
  function showSync(s) {
    if (shown(s.track)) s.cur = nearest(s.track, s.slides);   // a hidden slideshow keeps its state (first slide)
    s.slides.forEach(function (sl, i) { sl.classList.toggle('is-current', i === s.cur); });
    var st = counts(s);
    if (s.c.navStyle && s.c.navStyle !== 'none' && !s.c.loop) navState(s.prev, s.next, st.n, st.show, st.step, s.cur);
    if (s.dots.length) dotsState(s.dots, s.cur, st.step);
  }
  // Arrow steps, 1-based like the builder: next clamps so the last page is whole, loop wraps
  function showStep(s, dir) {
    var st = counts(s), n = st.n, cur = nearest(s.track, s.slides) + 1, to;
    if (dir > 0) {
      if (cur === n || cur + st.show - 1 === n) { if (!s.c.loop) return; to = 1; }
      else {
        to = Math.min(cur + st.step, n);
        if (to === n) to = n - (st.step - 1);
        if (to < n && to > n - st.show) to = n - st.show + 1;
      }
    } else if (cur === 1) { if (!s.c.loop) return; to = n - st.show + 1; }
    else to = Math.max(cur - st.step, 1);
    goTo(s.track, s.slides, to - 1, true);
  }
  // Autoplay every autoPlayDelay ms: advance by slidesToScroll; with loop, back to the first
  // slide once the last one (or the last whole page) is showing
  function startAuto(s) {
    stopAuto(s);
    s.timer = setInterval(function () {
      if (!s.track.isConnected) { stopAuto(s); return; }
      if (motionPaused || s.hover || !shown(s.track)) return;
      var st = counts(s), cur = nearest(s.track, s.slides), to = cur + st.step;
      if (s.c.loop && (cur + 1 === st.n || cur + 1 + st.show > st.n)) to = 0;
      goTo(s.track, s.slides, Math.min(to, st.n - 1), true);
    }, Math.max(500, num(s.c.autoPlayDelay, 3000)));
  }
  function stopAuto(s) { clearInterval(s.timer); s.timer = 0; }

  /* ------------------------------------------------------------------ video (HTML.Video2) */
  var pendingVideos = new Set();
  var loadedVideos = new WeakSet();
  var videoIO = watch(startVideo, { threshold: 0.3 });   // the builder's lazy video loads at 30% in view
  function videoOf(box) { return box.tagName === 'VIDEO' ? box : box.querySelector('video'); }
  function initVideos(root) {
    all(root, '[data-pf-type="HTML.Video2"],[data-pf-type="HTML.Video3"],.pf-media-bg-video').forEach(function (box) {
      var v = videoOf(box);
      if (!v || !once(box, 'video')) return;
      if (v.getAttribute('data-lazyloading') === 'lazy' && videoIO) { pendingVideos.add(box); videoIO.observe(box); }
      else startVideo(box);
    });
  }
  function playSafe(v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
  function setSource(v) {
    var src = v.getAttribute('data-src');
    if (src) { v.src = src; v.removeAttribute('data-src'); }
  }
  function startVideo(box) {
    var v = videoOf(box);
    if (!v || loadedVideos.has(box)) return;
    loadedVideos.add(box);
    pendingVideos.delete(box);
    var cover = box.querySelector('.pf-video-cover-button'), play = box.querySelector('.pf-play-button');
    var auto = v.hasAttribute('autoplay'), muted = v.hasAttribute('muted') || v.muted, controls = v.hasAttribute('controls');
    if (!cover || auto) setSource(v);
    if (v.readyState >= 2) v.classList.remove(HIDDEN);
    v.addEventListener('loadeddata', function () { v.classList.remove(HIDDEN); });
    if (play) {
      v.addEventListener('pause', function () { play.classList.remove(HIDDEN); });
      v.addEventListener('play', function () { play.classList.add(HIDDEN); });
    }
    if (!box.hasAttribute('data-pf-scrub-owned')) {
      box.addEventListener('click', function () {
        if (!v.getAttribute('src')) setSource(v);
        if (cover && !cover.classList.contains(HIDDEN)) { playSafe(v); cover.classList.add(HIDDEN); }
        else if (!controls) { if (v.paused) playSafe(v); else v.pause(); }   // play() after the end starts over
      });
    }
    if (auto && muted) {
      if (cover) cover.classList.add(HIDDEN);
      if (play) play.classList.add(HIDDEN);
      v.muted = true;
      playSafe(v);
    }
  }

  /* ------------------------------------------------------------------ lazy (backgrounds, data-src) */
  var pendingMedia = new Set();
  var bgIO = watch(function (el) { el.classList.remove('pf-bg-lazy'); }, { rootMargin: '5%' });
  var mediaIO = watch(showMedia, { rootMargin: '200px' });
  var LAZY = '.__pf img[data-src],.__pf img[data-srcset],.__pf iframe[data-src],.__pf source[data-srcset]';
  var bgTimer = 0;
  function initLazy(root) {
    all(root, '.pf-bg-lazy').forEach(function (el) {
      if (!once(el, 'bg')) return;
      if (bgIO) bgIO.observe(el); else el.classList.remove('pf-bg-lazy');
    });
    if (!bgTimer) bgTimer = setTimeout(function () { all(doc, '.pf-bg-lazy').forEach(function (el) { el.classList.remove('pf-bg-lazy'); }); }, 10000);
    all(root, LAZY).forEach(function (el) {
      if (el.closest('.jdgm-widget,.jdgm-carousel-wrapper,.sealsubs-target-element') || !once(el, 'media')) return;
      pendingMedia.add(el);
      if (mediaIO) mediaIO.observe(el.tagName === 'SOURCE' && el.parentElement ? el.parentElement : el);
      else showMedia(el);
    });
  }
  function showMedia(el) {
    var list = el.tagName === 'PICTURE' ? all(el, 'source[data-srcset],img[data-src],img[data-srcset]') : [el];
    list.forEach(function (m) {
      if (!pendingMedia.has(m)) return;
      pendingMedia.delete(m);
      if (m.tagName === 'IMG') m.addEventListener('load', function () { m.classList.remove(HIDDEN); }, { once: true });
      if (m.getAttribute('data-srcset')) { m.srcset = m.getAttribute('data-srcset'); m.removeAttribute('data-srcset'); }
      if (m.getAttribute('data-src')) { m.src = m.getAttribute('data-src'); m.removeAttribute('data-src'); }
      if (m.tagName !== 'IMG' && m.tagName !== 'SOURCE') m.classList.remove(HIDDEN);
    });
  }

  /* ------------------------------------------------------------------ description */
  // Descriptions lose pasted <meta charset>, <style> and <script>; data-compact="N" keeps the
  // first N words (lone punctuation and entities do not count) with a More / Less link unless
  // data-show-button="false"; the cut then ends with "..." instead (endWithDots below).
  var DESC = '[data-pf-type="ProductDescription"][data-product-type="content"],' +
    '[data-pf-type="CollectionDescription"][data-collection-type="description"],[data-pf-type="ArticleExcerpt"]';
  var JUNK = /<meta charset="utf-8"[^>]*>|<style>[\s\S]*?<\/style>|<script[\s\S]*?<\/script>/g;
  function initDescriptions(root) {
    all(root, DESC).forEach(function (el) {
      if (el.getAttribute('data-loaded-des') === 'true') return;
      el.setAttribute('data-loaded-des', 'true');
      var full = el.innerHTML.replace(JUNK, '');
      var limit = parseInt(el.getAttribute('data-compact'), 10);
      var button = el.getAttribute('data-show-button') !== 'false';
      var short = limit > 0 ? truncateWords(full, limit, button) : full;
      if (short.length >= full.length) { if (el.innerHTML !== full) el.innerHTML = full; return; }
      el.setAttribute('data-collapse', 'true');
      if (!button) { endWithDots(el, full, limit); return; }
      var more = (el.getAttribute('data-more') || '').trim() || 'More';
      var less = (el.getAttribute('data-less') || '').trim() || 'Less';
      var link = function (text) { var a = doc.createElement('a'); a.href = '#'; a.className = 'pf-read-more'; a.textContent = text; return ' ' + a.outerHTML; };
      el.innerHTML = short + link(more);
      el.addEventListener('click', function (e) {
        var a = e.target.closest && e.target.closest('.pf-read-more');
        if (!a || !el.contains(a)) return;
        e.preventDefault();
        var open = el.getAttribute('data-collapse') === 'true';
        el.innerHTML = open ? full + link(less) : short + link(more);
        el.setAttribute('data-collapse', open ? 'false' : 'true');
      });
    });
  }
  function isWord(w) {
    if (/^&[a-z0-9]+;$/i.test(w)) return false;
    return w.length > 1 || /[a-z0-9]/i.test(w);
  }
  // Word-limit an HTML string: walk the whitespace-separated tokens (tags split out), keep them
  // up to and including the limit-th word, then close the tags that are still open.
  function truncateWords(html, limit, ellipsis) {
    var words = html.replace(/<[^>]*>/g, ' ').trim().match(/\S+/g);
    if (!words || words.length <= limit) return html;
    var toks = html.replace(/</g, ' <').replace(/>/g, '> ').split(/\s/), out = [], n = 0, w = 0;
    for (var i = 0; i < toks.length && n < limit && w < words.length; i++) {
      var tok = toks[i];
      var bare = tok.replace(/<[^>]*>/g, '').replace(/^[^>]*>/, '').replace(/<[^>]*$/, '');
      if (bare === words[w]) {
        w++;
        if (isWord(bare)) {
          n++;
          if (ellipsis && n === limit) tok = tok.replace(bare, bare + '&hellip;');
        }
      }
      out.push(tok);
    }
    var res = out.join(' ').replace(/ </g, '<').replace(/> /g, '>'), open = [];
    (res.match(/<[^>]*>/g) || []).forEach(function (tag) {
      if (/\/>$/.test(tag) || /^<br\b/i.test(tag)) return;
      var m = /^<([a-z0-9\-_]+)/i.exec(tag);
      if (m) { open.push(m[1].toLowerCase()); return; }
      m = /^<\/([a-z0-9\-_]+)>/i.exec(tag);
      if (m && m[1].toLowerCase() === open[open.length - 1]) open.pop();
    });
    while (open.length) res += '</' + open.pop() + '>';
    return res;
  }
  // A cut without a More link (data-show-button="false", the home Weekly Special) ends with "..." and keeps
  // today's height (Jonah 2026-09-30). From today's cut: a p, li, h1-h6 or blockquote the cut ends in after
  // at most 3 of its words goes when words come before it (fragment); trailing , ; : . ! ? ( - en dash,
  // em dash and & go, then at most 3 small words, punctuation again after each, never below 5 words (clean
  // end); "..." joins the last word inside its element. Shown (el.getClientRects), h0 = the height of
  // today's cut: while the dotted text is taller than h0 + 0.5px its last word goes (at most 6 rounds), a
  // shorter one gets min-height h0. Hidden: text rules only. Every fit starts again from today's cut of the
  // kept original HTML: at init, after document.fonts.ready and on resize (one listener, 150 ms). A fit
  // fires 'preenpf:ending' on the element, detail {rules, drops, h0, height}.
  var ENDS = [];                                  // {el, full, limit} of every dotted description
  var endsResize = false, endsFonts = false;
  var BLOCKS = /^(p|li|h[1-6]|blockquote)$/i;
  var TRAIL = /[\s,;:.!?(\[{\-\u2013\u2014&\u2026\u201c\u2018\u00ab]+$/;
  var SMALL = /^(a|an|the|and|or|but|nor|of|to|for|with|in|on|at|by|from|into|as|than|so|our|your|their|its|this|these|that|those|is|are|we|you|it)$/i;
  function endWithDots(el, full, limit) {
    var d = null;
    ENDS = ENDS.filter(function (x) { if (x.el === el) d = x; return x.el.isConnected; });
    if (!d) { d = { el: el }; ENDS.push(d); }
    d.full = full;
    d.limit = limit;
    safe(fitEnd, d);
    if (!endsResize) { endsResize = true; window.addEventListener('resize', debounce(refitEnds, 150)); }
    if (!endsFonts && doc.fonts && doc.fonts.ready) {
      endsFonts = true;
      doc.fonts.ready.then(function () { endsFonts = false; refitEnds(); });
    }
  }
  function refitEnds() {
    ENDS = ENDS.filter(function (d) { return d.el.isConnected; });
    ENDS.forEach(function (d) { safe(fitEnd, d); });
  }
  function fitEnd(d) {
    var el = d.el, cut = truncateWords(d.full, d.limit, false), h0 = 0, h = 0, k = 0, r;
    el.style.minHeight = '';
    if (shown(el)) { el.innerHTML = cut; h0 = el.getBoundingClientRect().height; }
    r = dotEnd(el, cut, 0);
    if (h0) {
      h = el.getBoundingClientRect().height;
      while (h > h0 + 0.5 && k < 6) {
        r = dotEnd(el, cut, ++k);
        h = el.getBoundingClientRect().height;
        if (r.drops < k) break;                   // down to 5 words
      }
      if (h < h0) { el.style.minHeight = boxPx(el, h0); r.rules.push('min-height'); }
    }
    el.dispatchEvent(new CustomEvent('preenpf:ending', { detail: { rules: r.rules.length ? r.rules : ['plain'],
      drops: r.drops, h0: h0, height: h0 ? el.getBoundingClientRect().height : 0 } }));
  }
  // Today's cut in el, then fragment, clean end, `drops` more words off (clean end after each) and "..."
  function dotEnd(el, cut, drops) {
    var rules = [], k = 0, n;
    el.innerHTML = cut;
    if (dropFragment(el)) rules.push('fragment');
    cleanEnd(el, rules);
    while (k < drops && dropWord(el)) { k++; cleanEnd(el, rules); }
    if (k) rules.push('fit drop');
    n = lastText(el);
    if (n) n.data = n.data.replace(/\s+$/, '') + '...';
    return { rules: rules, drops: k };
  }
  function textNodes(el) {
    var out = [], w = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT), n;
    while ((n = w.nextNode())) out.push(n);
    return out;
  }
  function lastText(el) {
    var t = textNodes(el);
    for (var i = t.length - 1; i >= 0; i--) if (/\S/.test(t[i].data)) return t[i];
    return null;
  }
  // Words as the cut counts them: whitespace-separated, split at tags, lone punctuation not counted
  function countWords(el) {
    return textNodes(el).reduce(function (s, n) { return s + (n.data.match(/\S+/g) || []).filter(isWord).length; }, 0);
  }
  // Nothing after the last word: spaces, breaks, empty inline elements and list items left behind go
  function tidy(el) {
    var n = lastText(el), r;
    if (!n) return;
    r = doc.createRange();
    r.setStartAfter(n);
    r.setEnd(el, el.childNodes.length);
    r.deleteContents();
  }
  function addRule(rules, name) { if (rules.indexOf(name) < 0) rules.push(name); }
  // Trailing punctuation and symbols go; true when one did (not only spaces)
  function trimEnd(el) {
    var n, s, cut = false;
    while ((n = lastText(el))) {
      s = n.data.replace(TRAIL, '');
      if (/\S/.test(n.data.slice(s.length))) cut = true;
      n.data = s;
      if (/\S/.test(s)) break;
    }
    tidy(el);
    return cut;
  }
  function cleanEnd(el, rules) {
    if (trimEnd(el)) addRule(rules, 'punctuation');
    for (var i = 0; i < 3; i++) {
      var n = lastText(el), m = n && /(\S+)\s*$/.exec(n.data);
      if (!m || !SMALL.test(m[1]) || countWords(el) <= 5) return;
      n.data = n.data.slice(0, m.index);
      addRule(rules, 'small words');
      if (trimEnd(el)) addRule(rules, 'punctuation');
    }
  }
  function dropWord(el) {
    var n = lastText(el), m = n && /\S+\s*$/.exec(n.data);
    if (!m || countWords(el) <= 5) return false;
    n.data = n.data.slice(0, m.index);
    tidy(el);
    return true;
  }
  function dropFragment(el) {
    var n = lastText(el), b = n && n.parentNode, own;
    while (b && b !== el && !BLOCKS.test(b.nodeName)) b = b.parentNode;
    if (!b || b === el) return false;
    own = countWords(b);
    if (own > 3 || countWords(el) - own < 1) return false;
    b.parentNode.removeChild(b);
    tidy(el);                                     // and any ancestor left without words
    return true;
  }
  // min-height that makes the border box h px tall
  function boxPx(el, h) {
    var c = getComputedStyle(el);
    if (c.boxSizing !== 'border-box') {
      h -= num(parseFloat(c.paddingTop), 0) + num(parseFloat(c.paddingBottom), 0) +
        num(parseFloat(c.borderTopWidth), 0) + num(parseFloat(c.borderBottomWidth), 0);
    }
    return h + 'px';
  }

  /* ------------------------------------------------------------------ review links (home Judge.me carousels) */
  // fix-layout-6 (Jonah 2026-10-01): each review card in the home page's Judge.me carousels (the laptop/desktop
  // sidebar column and the phone/tablet block) links to where the review was posted: a product review to its
  // product page, a shop review imported from Google to the shop's Google reviews (new tab; Judge.me keeps no link
  // to the single Google post), any other review to the reviews page. One transparent link (a.preen-review-link,
  // styled in preen-fixes.css) lies over each card. The sidebar column itself is not a link (1.2.1, Jonah
  // 2026-10-01): its Google URL sits in data-preen-google-reviews, which only this module reads, so a click on the
  // heading, blank space or a gap opens nothing. The carousel arrows sit outside the cards and keep their own job.
  // Until the data arrives, or if it never does, every card links to the reviews page.
  // Data: Judge.me's public CDN endpoint (the host the Judge.me widget itself loads from), page 1 of
  // product-reviews and shop-reviews, then more pages (at most 3 per type) only while a card's review is still
  // unknown; read once, when a carousel first comes near the screen, and kept for the visit in sessionStorage.
  // Keyboard: the cards shown in the carousel window are Tab stops (the hidden ones get tabindex -1, so focus never
  // scrolls the window behind Judge.me's back); the arrows bring the next ones in.
  var RL_SCOPE = '.__pf_aiBmBgsz';                          // root of the home section (sections/preen-home.liquid)
  var RL_WRAP = '.jdgm-carousel-wrapper';
  var RL_ITEM = '.jdgm-carousel-item[data-review-id]';
  var RL_CLASS = 'preen-review-link';
  var RL_PAGE = '/pages/reviews-page';
  // the sidebar column's Google reviews URL (data-preen-google-reviews, not a link) is used when present; same URL
  var RL_GOOGLE = 'https://www.google.com/search?sca_esv=cd246a25237b15d7&hl=en-US&gl=us&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOaG6B046OqYmB1dQXFFFGJck7kHG2l1Q01P8xYaLgb2ghPksVC_LO1aZmFA1nlHMCwXMTUo0aLN2Nb1jPB93532jJqGj&q=Preen+Pets+Reviews&sa=X&ved=2ahUKEwiI5erqg_OTAxUymysGHYVZOKoQ0bkNegQIJhAH&biw=1710&bih=983&dpr=2#lrd=0x89cd89ff2863c721:0x76f1cf0d46e9214a,1,,,,';
  var RL_API = 'https://cdn.judge.me/reviews/all_reviews_js_based';
  var RL_SHOP = '4da9e1.myshopify.com';
  var RL_TYPES = ['product-reviews', 'shop-reviews'];
  var RL_MAX_PAGES = 3;
  var RL_KEY = 'preen-review-links:v1';
  var RL_PRODUCT = /^\/products\/[^\/?#\s]+\/?$/;
  var RL_GOOGLE_BADGE = 'review_written_in_google_business';
  // map: review id -> {k: 'p' product (h href, n title) | 'g' Google | 's' other}; pages / ended per type
  var rl = { map: {}, pages: {}, ended: {}, broken: {}, state: 'idle', io: null, restored: false, hosts: new Set(), timer: 0 };

  function rlShop() {
    var s = window.Shopify && window.Shopify.shop;
    return typeof s === 'string' && /^[a-z0-9-]+\.myshopify\.com$/.test(s) ? s : RL_SHOP;
  }
  function rlWrappers(root) {
    return all(root, RL_WRAP).filter(function (w) { return !!w.closest(RL_SCOPE); });
  }
  function rlGoogle() {
    var col = doc.querySelector(RL_SCOPE + ' [data-preen-google-reviews]');
    var h = col ? (col.getAttribute('data-preen-google-reviews') || '').trim() : '';
    return /^https:\/\/www\.google\.com\//.test(h) ? h : RL_GOOGLE;
  }
  function rlTarget(id) {
    var r = rl.map[id];
    if (r && r.k === 'p' && RL_PRODUCT.test(r.h || '')) {
      return { href: r.h, label: r.n ? 'Read about ' + r.n : 'See this product', kind: 'product' };
    }
    if (r && r.k === 'g') return { href: rlGoogle(), label: 'See this review on Google (opens in a new tab)', kind: 'google', blank: true };
    return { href: RL_PAGE, label: 'See all reviews', kind: 'reviews-page' };
  }
  function rlSet(el, name, value) {
    if (value == null) { if (el.hasAttribute(name)) el.removeAttribute(name); }
    else if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }
  function rlCard(item) {
    var a = kid(item, 'a.' + RL_CLASS);
    if (!a) {
      a = doc.createElement('a');
      a.className = RL_CLASS;
      item.appendChild(a);
    }
    var t = rlTarget(item.getAttribute('data-review-id'));
    rlSet(a, 'href', t.href);
    rlSet(a, 'aria-label', t.label);
    rlSet(a, 'target', t.blank ? '_blank' : null);
    rlSet(a, 'rel', t.blank ? 'noopener' : null);
    rlSet(a, 'data-review-link', t.kind);
  }
  // Tab stops: the cards at least half inside the carousel window
  function rlRove(wrap) {
    var box = wrap && wrap.querySelector('.jdgm-carousel__item-container');
    if (!box || !shown(box)) return;
    var b = box.getBoundingClientRect();
    all(wrap, RL_ITEM).forEach(function (item) {
      var a = kid(item, 'a.' + RL_CLASS);
      if (!a) return;
      var r = item.getBoundingClientRect();
      var inside = Math.min(r.bottom, b.bottom) - Math.max(r.top, b.top) >= r.height / 2 - 1 &&
        Math.min(r.right, b.right) - Math.max(r.left, b.left) >= r.width / 2 - 1;
      rlSet(a, 'tabindex', inside ? null : '-1');
    });
  }
  function rlScan(wrap) {
    all(wrap, RL_ITEM).forEach(rlCard);
    rlRove(wrap);
    var box = wrap.querySelector('.jdgm-carousel__item-container');
    // the window is overflow:hidden and Judge.me moves the strip with a transform: nothing may scroll it
    if (box && once(box, 'reviewScroll')) {
      box.addEventListener('scroll', function () {
        if ((box.scrollTop || box.scrollLeft) && getComputedStyle(box).overflowY === 'hidden') {
          box.scrollTop = 0;
          box.scrollLeft = 0;
        }
      }, { passive: true });
    }
  }
  function rlAll() { rlWrappers(doc).forEach(function (w) { safe(rlScan, w); }); }
  // Judge.me re-renders, clones or moves cards: scan the block again once the changes settle
  function rlSoon(host, ms) {
    rl.hosts.add(host);
    clearTimeout(rl.timer);
    rl.timer = setTimeout(function () {
      var hosts = Array.from(rl.hosts);
      rl.hosts.clear();
      hosts.forEach(function (h) { if (h.isConnected) initReviewLinks(h); });
    }, ms || 80);
  }
  function rlWatch(wrap) {
    var host = wrap.parentElement || wrap;
    if (!once(host, 'reviewLinks')) return;
    if (window.MutationObserver) {
      new MutationObserver(function () { rlSoon(host); })
        .observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    }
    host.addEventListener('transitionend', function () { rlWrappers(host).forEach(rlRove); });
  }

  function rlStore(save) {
    try {
      var s = window.sessionStorage;
      if (save) {
        s.setItem(RL_KEY, JSON.stringify({ shop: rlShop(), map: rl.map, pages: rl.pages, ended: rl.ended }));
        return null;
      }
      return JSON.parse(s.getItem(RL_KEY) || 'null');
    } catch (e) {
      return null;
    }
  }
  function rlRestore() {
    var c = rlStore(false);
    if (!c || c.shop !== rlShop() || !c.map || typeof c.map !== 'object') return;
    rl.map = c.map;
    rl.pages = c.pages && typeof c.pages === 'object' ? c.pages : {};
    rl.ended = c.ended && typeof c.ended === 'object' ? c.ended : {};
  }
  function rlUnknown() {
    var out = [];
    rlWrappers(doc).forEach(function (w) {
      all(w, RL_ITEM).forEach(function (it) {
        var id = it.getAttribute('data-review-id');
        if (id && !rl.map[id] && out.indexOf(id) < 0) out.push(id);
      });
    });
    return out;
  }
  // One page of the endpoint's HTML: .jdgm-rev[data-review-id]; product reviews carry .jdgm-rev__prod-link,
  // Google-imported shop reviews the transparency badge review_written_in_google_business. Only a /products/ path
  // is taken from the data; every other target is this script's own.
  function rlParse(type, html) {
    var d = new DOMParser().parseFromString(String(html || ''), 'text/html');
    var revs = d.querySelectorAll('.jdgm-rev[data-review-id]');
    Array.prototype.forEach.call(revs, function (rev) {
      var id = rev.getAttribute('data-review-id');
      if (!id || rl.map[id]) return;
      if (type === 'product-reviews') {
        var a = rev.querySelector('a.jdgm-rev__prod-link');
        var h = a ? (a.getAttribute('href') || '').trim() : '';
        rl.map[id] = RL_PRODUCT.test(h) ?
          { k: 'p', h: h.replace(/\/$/, ''), n: (a.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 120) } : { k: 's' };
      } else {
        rl.map[id] = { k: rev.querySelector('[data-badge-type="' + RL_GOOGLE_BADGE + '"]') ? 'g' : 's' };
      }
    });
    return revs.length;
  }
  function rlGet(type, page) {
    var shop = encodeURIComponent(rlShop());
    var url = RL_API + '?url=' + shop + '&shop_domain=' + shop + '&platform=shopify&review_type=' + type + '&page=' + page;
    var ctl = window.AbortController ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, 10000);
    return fetch(url, { credentials: 'omit', signal: ctl ? ctl.signal : undefined })
      .then(function (r) {
        if (!r.ok) throw new Error('review list: HTTP ' + r.status);
        return r.json();
      })
      .then(function (j) {
        clearTimeout(timer);
        if (!j || typeof j.html !== 'string') throw new Error('review list: no html');
        rl.pages[type] = page;
        if (!rlParse(type, j.html)) rl.ended[type] = true;
      }, function (e) {
        clearTimeout(timer);
        throw e;
      });
  }
  function rlLoad() {
    if (rl.state !== 'idle') return;
    rl.state = 'loading';
    if (!window.fetch || !window.DOMParser) { rl.state = 'failed'; return; }
    var round = function () {
      var unknown = rlUnknown();
      var todo = RL_TYPES.filter(function (t) {
        var p = num(rl.pages[t], 0);
        return !rl.ended[t] && !rl.broken[t] && p < RL_MAX_PAGES && (p === 0 || unknown.length > 0);
      });
      if (!todo.length) {
        rl.state = RL_TYPES.some(function (t) { return rl.broken[t]; }) ? 'failed' : 'ready';
        rlAll();
        return;
      }
      Promise.all(todo.map(function (t) {
        return rlGet(t, num(rl.pages[t], 0) + 1).then(function () { return true; }, function (e) {
          rl.broken[t] = true;                     // not stored: the next page view tries again
          if (window.console) console.warn('[PreenPF]', e && e.message ? e.message : e);
          return false;
        });
      })).then(function (ok) {
        if (ok.some(Boolean)) rlStore(true);
        rlAll();
        round();
      });
    };
    round();
  }
  function initReviewLinks(root) {
    var wraps = rlWrappers(root);
    if (!wraps.length) return;
    if (!rl.restored) {
      rl.restored = true;
      rlRestore();
      window.addEventListener('resize', debounce(function () { rlWrappers(doc).forEach(rlRove); }, 150));
    }
    if (!rl.io && hasIO) rl.io = watch(function () { rlLoad(); }, { rootMargin: '400px 0px' });
    wraps.forEach(function (w) {
      rlWatch(w);
      rlScan(w);
      if (rl.io && once(w, 'reviewLoad')) rl.io.observe(w);
    });
    if (!hasIO) rlLoad();
  }

  /* ------------------------------------------------------------------ boot and harness API */
  function init(root) {
    root = root && root.querySelectorAll ? root : doc;
    safe(initDescriptions, root);
    safe(initLazy, root);
    safe(initVideos, root);
    safe(initGalleries, root);
    safe(initSlideshows, root);
    safe(bindAtc, root);
    safe(bindSwatches, root);
    safe(initVariants, root);
    safe(initReviewLinks, root);
  }
  function loadAll() {
    all(doc, '.pf-bg-lazy').forEach(function (el) { el.classList.remove('pf-bg-lazy'); });
    Array.from(pendingMedia).forEach(function (m) {
      if (mediaIO) mediaIO.unobserve(m);
      showMedia(m.tagName === 'SOURCE' && m.parentElement && m.parentElement.tagName === 'PICTURE' ? m.parentElement : m);
    });
    // lazy videos load when they are on the page; one inside a block hidden at this width stays as a visitor gets it
    Array.from(pendingVideos).forEach(function (box) {
      if (!shown(box)) return;
      if (videoIO) videoIO.unobserve(box);
      startVideo(box);
    });
  }
  function pause() {
    motionPaused = true;
    shows.forEach(stopAuto);
    all(doc, 'video').forEach(function (v) { if (!v.paused) v.pause(); });
  }
  function resume() {
    motionPaused = false;
    shows.forEach(function (s) { if (s.c.autoPlay && !s.timer && s.track.isConnected) startAuto(s); });
  }
  function resetSliders() {
    motionPaused = true;
    shows.forEach(function (s) {
      stopAuto(s);
      if (!s.track.isConnected) return;
      s.track.scrollTo({ left: 0, behavior: 'instant' });
      s.cur = 0;
      showSync(s);
    });
    galleries.forEach(function (g) {
      if (!g.mt.isConnected) return;
      g.mt.scrollTo({ left: 0, behavior: 'instant' });
      if (g.lt) g.lt.scrollTo({ left: 0, behavior: 'instant' });
      g.cur = 0;
      markGallery(g);
      markThumbs(g);
    });
  }

  window.PreenPF = {
    version: VERSION,
    init: function (root) { init(root || doc); },
    loadAll: loadAll,
    pause: pause,
    resume: resume,
    resetSliders: resetSliders
  };
  doc.addEventListener('click', onDataHref);
  doc.addEventListener('shopify:section:load', function (e) { init(e.target); });
  var boot = function () { init(doc); };
  var me = doc.currentScript;
  // As a deferred theme script this runs before DOMContentLoaded: wait for it, so inline
  // DOMContentLoaded handlers (the B2B price swap rewrites .b2b-price markup) run first.
  if (doc.readyState === 'loading' || (me && me.src && me.defer && doc.readyState === 'interactive')) {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot);
})();
